import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Typography, 
  Card, 
  Space, 
  Tag, 
  Button, 
  Input, 
  Select, 
  Row, 
  Col, 
  Tooltip, 
  message, 
  Empty, 
  Badge,
  Switch,
  DatePicker
} from 'antd';
import { 
  CodeOutlined, 
  SyncOutlined, 
  CopyOutlined, 
  SearchOutlined, 
  BugOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { SystemHealthCard } from '../components/Dashboard/SystemHealthCard';
import { dashboardService, SystemLogEntry } from '../services/dashboardService';
import { exportToCSV } from '../utils/exportUtils';

const { Title, Text } = Typography;
const { Option } = Select;

const COMPONENT_PATTERNS: Record<string, string[]> = {
  auth: ['auth', 'login', 'logout', 'token', 'jwt', 'user', 'session', 'password', 'oauth'],
  interventions: ['intervention', 'technician', 'technicien', 'mission', 'assign', 'rapport'],
  payments: ['payment', 'paiement', 'cinetpay', 'wave', 'orange', 'mtn', 'moov', 'stripe', 'transaction', 'solde'],
  chat: ['chat', 'message', 'socket', 'conversation', 'push'],
  quotes: ['quote', 'devis', 'invoice', 'facture', 'estimate'],
  database: ['db', 'postgres', 'sequelize', 'query', 'sql', 'pool', 'connection', 'migration', 'database']
};

export const SystemLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<SystemLogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [logType, setLogType] = useState<'error' | 'combined'>('combined');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [componentFilter, setComponentFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<Dayjs | null>(null);
  const [lineLimit, setLineLimit] = useState<number>(100);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    return localStorage.getItem('system_logs_auto_refresh') === 'true';
  });
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [expandedLogIndex, setExpandedLogIndex] = useState<number | null>(null);

  const matchesComponent = useCallback((log: SystemLogEntry, comp: string) => {
    if (comp === 'all') return true;
    const patterns = COMPONENT_PATTERNS[comp] || [];
    const textToSearch = `${log.message || ''} ${log.url || ''} ${log.level || ''} ${JSON.stringify(log.meta || '')} ${log.stack || ''}`.toLowerCase();
    return patterns.some(p => textToSearch.includes(p));
  }, []);

  const matchesDate = useCallback((log: SystemLogEntry, targetDate: Dayjs | null) => {
    if (!targetDate) return true;
    if (!log.timestamp) return false;
    return dayjs(log.timestamp).isSame(targetDate, 'day');
  }, []);

  const displayedLogs = useMemo(() => {
    return logs.filter(log => matchesComponent(log, componentFilter) && matchesDate(log, dateFilter));
  }, [logs, componentFilter, dateFilter, matchesComponent, matchesDate]);

  const handleExport = () => {
    try {
      setExporting(true);
      if (displayedLogs.length === 0) {
        message.warning('Aucun log à exporter.');
        return;
      }
      const dataToExport = displayedLogs.map(log => {
        let detectedComp = 'Général';
        for (const [key, patterns] of Object.entries(COMPONENT_PATTERNS)) {
          const textToSearch = `${log.message || ''} ${log.url || ''} ${JSON.stringify(log.meta || '')}`.toLowerCase();
          if (patterns.some(p => textToSearch.includes(p))) {
            detectedComp = key;
            break;
          }
        }
        return {
          Timestamp: log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR') : '',
          Niveau: (log.level || 'INFO').toUpperCase(),
          Composant: detectedComp,
          Methode: log.method || '',
          URL: log.url || '',
          Correlation_ID: log.correlationId || '',
          Message: (log.message || log.stack || '').replace(/\r?\n/g, ' ')
        };
      });
      exportToCSV(dataToExport, `logs_systeme_${logType}`);
      message.success(`${displayedLogs.length} logs exportés avec succès !`);
    } catch (err) {
      console.error('Erreur export logs:', err);
      message.error("Erreur lors de l'exportation des logs.");
    } finally {
      setExporting(false);
    }
  };

  const fetchLogs = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    setIsRefreshing(true);
    try {
      const res = await dashboardService.getSystemLogs({
        type: logType,
        lines: lineLimit,
        level: levelFilter,
        search: searchQuery
      });
      if (res.success) {
        setLogs(res.logs || []);
        setTotal(res.total || 0);
      } else {
        if (!isSilent) message.error(res.message || 'Erreur lors du chargement des logs');
      }
    } catch (err) {
      console.error('Erreur chargement logs:', err);
      if (!isSilent) message.error('Impossible de contacter le serveur');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [logType, lineLimit, levelFilter, searchQuery]);

  useEffect(() => {
    fetchLogs(false);
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchLogs(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  const handleAutoRefreshToggle = (checked: boolean) => {
    setAutoRefresh(checked);
    localStorage.setItem('system_logs_auto_refresh', String(checked));
    if (checked) {
      message.success('Auto-rafraîchissement activé (toutes les 10s)');
    } else {
      message.info('Auto-rafraîchissement désactivé');
    }
  };

  const handleCopyLog = (log: SystemLogEntry) => {
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
    message.success('Log copié dans le presse-papier !');
  };

  const getLevelBadge = (level: string) => {
    const lvl = (level || 'info').toLowerCase();
    if (lvl === 'error') {
      return <Tag color="error" icon={<BugOutlined />}>ERROR</Tag>;
    }
    if (lvl === 'warn' || lvl === 'warning') {
      return <Tag color="warning" icon={<ExclamationCircleOutlined />}>WARN</Tag>;
    }
    return <Tag color="processing" icon={<InfoCircleOutlined />}>INFO</Tag>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 24 }}>
      {/* Widget État du Système */}
      <SystemHealthCard />

      {/* En-tête de la Console */}
      <Card bodyStyle={{ padding: '20px 24px' }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <CodeOutlined style={{ color: '#0a543d' }} /> Console de Logs Système
            </Title>
            <Text type="secondary">
              Supervision des logs et exceptions du serveur VPS en temps réel
            </Text>
          </Col>
          <Col xs={24} md={10} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
            <Space>
              <Text type="secondary" style={{ fontSize: 12 }}>Auto-rafraîchissement (10s)</Text>
              <Switch checked={autoRefresh} onChange={handleAutoRefreshToggle} size="small" />
            </Space>
            <Button 
              type="primary" 
              icon={<SyncOutlined spin={loading || isRefreshing} />} 
              onClick={() => fetchLogs(false)}
              style={{ backgroundColor: '#0a543d', borderColor: '#0a543d', borderRadius: 8 }}
            >
              Rafraîchir
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Barre de Filtres & Recherche */}
      <Card bodyStyle={{ padding: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Fichier de Log :</Text>
            <Select 
              value={logType} 
              onChange={(val) => setLogType(val as 'error' | 'combined')} 
              style={{ width: '100%' }}
            >
              <Option value="error">Fichier Erreurs (error.log)</Option>
              <Option value="combined">Tous les Logs (combined.log)</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Niveau de sévérité :</Text>
            <Select 
              value={levelFilter} 
              onChange={setLevelFilter} 
              style={{ width: '100%' }}
            >
              <Option value="all">Tous les niveaux</Option>
              <Option value="error">Erreurs (ERROR)</Option>
              <Option value="warn">Avertissements (WARN)</Option>
              <Option value="info">Informations (INFO)</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              <AppstoreOutlined /> Composant :
            </Text>
            <Select 
              value={componentFilter} 
              onChange={setComponentFilter} 
              style={{ width: '100%' }}
            >
              <Option value="all">Tous les composants</Option>
              <Option value="auth">Authentification (auth, tokens)</Option>
              <Option value="interventions">Interventions & Techniciens</Option>
              <Option value="payments">Paiements (Wave, Orange, MTN...)</Option>
              <Option value="chat">Chat / Messagerie</Option>
              <Option value="quotes">Devis & Facturation</Option>
              <Option value="database">Base de données (PostgreSQL)</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              <CalendarOutlined /> Date :
            </Text>
            <DatePicker 
              value={dateFilter} 
              onChange={setDateFilter} 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY" 
              placeholder="Toutes les dates" 
              allowClear 
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle" style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Nombre de lignes :</Text>
            <Select 
              value={lineLimit} 
              onChange={setLineLimit} 
              style={{ width: '100%' }}
            >
              <Option value={50}>50 lignes</Option>
              <Option value={100}>100 lignes</Option>
              <Option value={200}>200 lignes</Option>
              <Option value={500}>500 lignes</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={14}>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Recherche textuelle / Correlation ID :</Text>
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Chercher une erreur, un IP, un URL ou un ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={24} md={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', paddingTop: 20 }}>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={handleExport}
              loading={exporting}
              disabled={displayedLogs.length === 0}
              style={{ borderRadius: 8, width: '100%' }}
            >
              Exporter CSV ({displayedLogs.length})
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Terminal de Logs Sombre (Console Output) */}
      <Card 
        bodyStyle={{ 
          padding: 0, 
          backgroundColor: '#0f172a', 
          borderRadius: 12,
          overflow: 'hidden'
        }}
      >
        {/* Terminal Header */}
        <div style={{
          backgroundColor: '#1e293b',
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #334155',
          flexWrap: 'wrap',
          gap: 10
        }}>
          <Space>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
            </div>
            <Text style={{ color: '#94a3b8', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>
              bash ~ {logType}.log ({displayedLogs.length} affiché{displayedLogs.length > 1 ? 's' : ''} sur {logs.length} chargés / {total} total)
            </Text>
          </Space>

          <Space wrap>
            {componentFilter !== 'all' && (
              <Tag color="purple">Composant: {componentFilter}</Tag>
            )}
            {dateFilter && (
              <Tag color="orange">Date: {dateFilter.format('DD/MM/YYYY')}</Tag>
            )}
            {searchQuery && (
              <Badge count={`Filtre: "${searchQuery}"`} style={{ backgroundColor: '#0284c7' }} />
            )}
          </Space>
        </div>

        {/* Terminal Content Body */}
        <div style={{
          padding: 16,
          maxHeight: 600,
          overflowY: 'auto',
          fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
          fontSize: 13,
          lineHeight: '1.6',
          color: '#e2e8f0'
        }}>
          {displayedLogs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Empty 
                description={<span style={{ color: '#94a3b8' }}>Aucun log disponible pour ce filtre</span>} 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            displayedLogs.map((log, index) => {
              const isExpanded = expandedLogIndex === index;
              const dateStr = log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR') : '';

              return (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: 12,
                    padding: 10,
                    borderRadius: 6,
                    backgroundColor: isExpanded ? '#1e293b' : 'transparent',
                    borderLeft: log.level === 'error' ? '4px solid #ef4444' : (log.level === 'warn' ? '4px solid #f59e0b' : '4px solid #3b82f6'),
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <Space wrap style={{ alignItems: 'center' }}>
                      <Text style={{ color: '#64748b', fontSize: 11 }}>#{displayedLogs.length - index}</Text>
                      {getLevelBadge(log.level)}
                      <Text style={{ color: '#38bdf8', fontSize: 12 }}>[{dateStr}]</Text>

                      {log.correlationId && (
                        <Tag color="cyan" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                          ID: {log.correlationId}
                        </Tag>
                      )}

                      {log.method && log.url && (
                        <Tag color="purple" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {log.method} {log.url}
                        </Tag>
                      )}
                    </Space>

                    <Space size="small">
                      <Tooltip title="Copier cette ligne de log">
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<CopyOutlined style={{ color: '#94a3b8' }} />} 
                          onClick={() => handleCopyLog(log)}
                        />
                      </Tooltip>
                      <Button 
                        type="text" 
                        size="small" 
                        style={{ color: '#38bdf8', fontSize: 12 }}
                        onClick={() => setExpandedLogIndex(isExpanded ? null : index)}
                      >
                        {isExpanded ? 'Réduire' : 'Détails JSON'}
                      </Button>
                    </Space>
                  </div>

                  {/* Message principal */}
                  <div style={{ marginTop: 6, color: log.level === 'error' ? '#fca5a5' : '#fef08a', fontWeight: 500 }}>
                    {log.message || log.stack || 'Aucun message'}
                  </div>

                  {/* Détails JSON étendus */}
                  {isExpanded && (
                    <pre style={{
                      marginTop: 10,
                      padding: 12,
                      backgroundColor: '#090d16',
                      borderRadius: 6,
                      color: '#a7f3d0',
                      fontSize: 12,
                      overflowX: 'auto',
                      border: '1px solid #334155'
                    }}>
                      {JSON.stringify(log, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
};

export default SystemLogsPage;
